import { Drawer, Input, InputNumber, Select, message, Switch } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPfPolicy, fetchAllPfPolicies, updatePfPolicy } from "../../../core/data/redux/payroll/Accounting/providentFund/pfpolicies/pfPolicyActions";
import { AppDispatch } from "../../../core/data/redux/store";
import { status } from "../../../core/common/selectoption/selectoption";

const PfPolicyModal: React.FC<any> = ({ modalType, handleCancel, isModalVisible, selectedRowData }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loadingStates } = useSelector((state: any) => state.providentFund.pfPolicy);

    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;

    const [formData, setFormData] = useState({
        policyName: "",
        employeeContributionPercent: 0,
        employerContributionPercent: 0,
        maxWageLimit: 0,
        isVoluntary: false,
        createdBy: user?.UserId || 1,
        status: "active",
    });

    const [errors, setErrors] = useState<any>({});
    const resetForm = useCallback(() => {
        setFormData({
            policyName: "",
            employeeContributionPercent: 0,
            employerContributionPercent: 0,
            maxWageLimit: 0,
            isVoluntary: false,
            createdBy: user?.UserId || 1,
            status: "active",
        });
        setErrors({});
    }, [user?.UserId]);

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                policyName: selectedRowData.policyName || "",
                employeeContributionPercent: selectedRowData.employeeContributionPercent || 0,
                employerContributionPercent: selectedRowData.employerContributionPercent || 0,
                maxWageLimit: selectedRowData.maxWageLimit || 0,
                isVoluntary: selectedRowData.isVoluntary || false,
                createdBy: selectedRowData.createdBy || user?.UserId || 1,
                status: selectedRowData.status || "active",

            });
            setErrors({});
        } else {
            resetForm();
        }
    }, [modalType, resetForm, selectedRowData, user?.UserId]);



    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors: any = {};

        if (!formData.policyName.trim()) {
            newErrors.policyName = "Policy name is required.";
            valid = false;
        }

        if (formData.employeeContributionPercent <= 0) {
            newErrors.employeeContributionPercent = "Employee contribution must be greater than 0.";
            valid = false;
        }

        if (formData.employerContributionPercent <= 0) {
            newErrors.employerContributionPercent = "Employer contribution must be greater than 0.";
            valid = false;
        }
        if (!formData.status) {
            newErrors.status = "Status is required.";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            if (modalType === "edit") {
                await dispatch(updatePfPolicy({ id: selectedRowData.pfPolicyId, data: formData })).unwrap();
                message.success("PF policy updated successfully");
            } else {
                await dispatch(createPfPolicy(formData)).unwrap();
                message.success("PF policy created successfully");
            }
            handleCancel();
            resetForm();
            await dispatch(fetchAllPfPolicies())
        } catch (error) {
            message.error("Something went wrong. Please try again.");
        }
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit PF Policy" : "Add PF Policy"}
            placement="right"
            width={500}
            style={{ marginTop: 50, paddingBottom: 50 }}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
        >
            <form>
                <div className="mb-3">
                    <label className="form-label">Policy Name</label>
                    <Input
                        value={formData.policyName}
                        onChange={(e) => handleChange("policyName", e.target.value)}
                    />
                    {errors.policyName && <div className="text-danger mt-1">{errors.policyName}</div>}
                </div>

                <div className="mb-3">
                    <label className="form-label">Employee Contribution (%)</label>
                    <InputNumber
                        min={0}
                        value={formData.employeeContributionPercent}
                        onChange={(value) => handleChange("employeeContributionPercent", value)}
                        style={{ width: "100%" }}
                    />
                    {errors.employeeContributionPercent && (
                        <div className="text-danger mt-1">{errors.employeeContributionPercent}</div>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label">Employer Contribution (%)</label>
                    <InputNumber
                        min={0}
                        value={formData.employerContributionPercent}
                        onChange={(value) => handleChange("employerContributionPercent", value)}
                        style={{ width: "100%" }}
                    />
                    {errors.employerContributionPercent && (
                        <div className="text-danger mt-1">{errors.employerContributionPercent}</div>
                    )}
                </div>
                <div className="mb-3">
                    <label className="form-label">Status</label>
                    <Select
                        className="w-100"
                        value={formData.status}
                        onChange={(value) => handleChange("status", value)}
                    >
                        {status.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
                </div>

                <div className="mb-3">
                    <label className="form-label">Is Voluntary</label>
                    <div>
                        <Switch
                            checked={formData.isVoluntary}
                            onChange={(checked) => handleChange("isVoluntary", checked)}
                        />
                    </div>
                </div>


                <div className="modal-footer mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                            handleCancel();
                            resetForm();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update PF Policy" : "Add PF Policy"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default PfPolicyModal;
